import type { HistoryResult } from '@creanizer/types'
import '../styles/documentStyles.css'

interface HistoryViewerProps {
  document: HistoryResult
  visualStyle?: string
}

export function HistoryViewer({ document, visualStyle = 'modern' }: HistoryViewerProps) {
  // データ検証
  if (!document || !document.title) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">歴史書</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 古文書・巻物スタイル
  if (visualStyle === 'scroll') {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-gradient-to-b from-yellow-100 to-yellow-50 shadow-lg relative overflow-hidden">
        {/* 巻物の装飾 */}
        <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-yellow-800 to-yellow-600 opacity-30"></div>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-yellow-800 to-yellow-600 opacity-30"></div>
        
        <div className="relative z-10 py-8">
          <h1 className="text-3xl font-bold text-center mb-6 text-yellow-900" style={{ fontFamily: 'serif' }}>
            {document.title}
          </h1>
          
          {document.author && (
            <div className="text-center mb-6 text-yellow-800">
              <div>{document.author.name}</div>
              {document.author.title && (
                <div className="text-sm">{document.author.title}</div>
              )}
            </div>
          )}
          
          {document.preface && (
            <div className="mb-8 text-yellow-900">
              <h2 className="text-xl font-bold mb-3">序</h2>
              <p className="whitespace-pre-wrap leading-relaxed">{document.preface}</p>
            </div>
          )}
          
          {document.chapters && document.chapters.length > 0 ? (
            <div className="space-y-6">
              {document.chapters.map((chapter, index) => (
                <div key={index} className="text-yellow-900">
                  <h3 className="text-lg font-bold mb-2">
                    {chapter.number && `第${chapter.number}章 `}{chapter.title}
                  </h3>
                  <p className="whitespace-pre-wrap leading-relaxed mb-2">
                    {chapter.content}
                  </p>
                  {chapter.annotations && chapter.annotations.length > 0 && (
                    <div className="ml-4 text-sm text-yellow-700 space-y-1">
                      {chapter.annotations.map((note, nIndex) => (
                        <div key={nIndex}>※ {note}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : document.body && (
            <div className="text-yellow-900 whitespace-pre-wrap leading-relaxed">
              {document.body}
            </div>
          )}
          
          {document.epilogue && (
            <div className="mt-8 text-yellow-900">
              <h2 className="text-xl font-bold mb-3">跋</h2>
              <p className="whitespace-pre-wrap leading-relaxed">{document.epilogue}</p>
            </div>
          )}
          
          {document.colophon && (
            <div className="mt-8 text-center text-sm text-yellow-700">
              {document.colophon}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // 古代文書スタイル
  if (visualStyle === 'ancient') {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-amber-100 via-amber-50 to-yellow-50 shadow-lg relative">
        {/* 古びた紙の効果 */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full bg-gradient-to-br from-brown-600 to-amber-800"></div>
        </div>
        
        <div className="relative z-10">
          <div className="border-4 border-double border-amber-800 p-6">
            <h1 className="text-4xl font-bold text-center mb-6 text-amber-900" style={{ fontFamily: 'serif', letterSpacing: '0.2em' }}>
              {document.title}
            </h1>
            
            {document.metadata?.documentAge && (
              <div className="text-center text-sm text-amber-700 mb-4">
                [{document.metadata.documentAge}]
              </div>
            )}
            
            {document.author && (
              <div className="text-center mb-6 text-amber-800">
                <div className="text-lg">{document.author.name}</div>
                {document.author.era && (
                  <div className="text-sm">（{document.author.era}）</div>
                )}
              </div>
            )}
            
            <div className="text-amber-900 space-y-6">
              {document.chapters && document.chapters.length > 0 ? (
                document.chapters.map((chapter, index) => (
                  <div key={index}>
                    <h3 className="text-xl font-bold mb-3 text-center">
                      {chapter.title}
                    </h3>
                    <p className="whitespace-pre-wrap leading-relaxed text-justify">
                      {chapter.content}
                    </p>
                  </div>
                ))
              ) : document.body && (
                <p className="whitespace-pre-wrap leading-relaxed text-justify">
                  {document.body}
                </p>
              )}
            </div>
            
            {document.annotations && document.annotations.length > 0 && (
              <div className="mt-8 pt-4 border-t-2 border-amber-300">
                <h3 className="text-sm font-bold text-amber-800 mb-2">註釈</h3>
                <div className="space-y-1 text-sm text-amber-700">
                  {document.annotations.map((annotation, index) => (
                    <div key={index}>
                      {annotation.marker}: {annotation.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {document.metadata?.condition && (
              <div className="mt-6 text-xs text-center text-amber-600">
                保存状態: {document.metadata.condition}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
  
  // 中世写本スタイル
  if (visualStyle === 'medieval') {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-gradient-to-b from-amber-50 to-white shadow-lg">
        <div className="border-8 border-double border-red-800 p-8">
          {/* 装飾的な頭文字 */}
          <h1 className="text-5xl font-bold text-center mb-8 text-red-900" style={{ fontFamily: 'serif' }}>
            {document.title}
          </h1>
          
          {document.author && (
            <div className="text-center mb-6 text-red-800">
              <div className="text-xl">{document.author.name}</div>
              {document.author.title && (
                <div className="text-sm italic">{document.author.title}</div>
              )}
            </div>
          )}
          
          <div className="text-gray-900">
            {document.preface && (
              <div className="mb-8">
                <div className="text-6xl font-bold float-left mr-2 mt-1 text-red-800 leading-none">
                  {document.preface.charAt(0)}
                </div>
                <p className="text-justify leading-relaxed">
                  {document.preface.substring(1)}
                </p>
              </div>
            )}
            
            {document.chapters && document.chapters.length > 0 ? (
              <div className="space-y-8">
                {document.chapters.map((chapter, index) => (
                  <div key={index}>
                    <h2 className="text-2xl font-bold text-red-800 mb-4 text-center">
                      {chapter.title}
                    </h2>
                    <div className="columns-2 gap-8 text-justify leading-relaxed">
                      <p>{chapter.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : document.body && (
              <div className="columns-2 gap-8 text-justify leading-relaxed">
                <p>{document.body}</p>
              </div>
            )}
          </div>
          
          {document.colophon && (
            <div className="mt-8 text-center text-sm text-red-700 italic">
              {document.colophon}
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // デフォルト（現代の歴史書）
  return (
    <div className="max-w-5xl mx-auto p-8 bg-white shadow-lg">
      <header className="mb-8 pb-6 border-b-2 border-gray-300">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {document.title}
        </h1>
        
        {document.era && (
          <div className="text-sm text-gray-600 mb-2">
            時代: {document.era}
          </div>
        )}
        
        {document.author && (
          <div className="text-gray-700">
            <span>著者: </span>
            <span className="font-medium">{document.author.name}</span>
            {document.author.title && (
              <span className="ml-2 text-sm">（{document.author.title}）</span>
            )}
          </div>
        )}
        
        {document.documentType && (
          <div className="text-sm text-gray-600 mt-2">
            文書形式: {document.documentType}
          </div>
        )}
      </header>
      
      {document.preface && (
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">序文</h2>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {document.preface}
          </p>
        </section>
      )}
      
      {document.chapters && document.chapters.length > 0 ? (
        <div className="space-y-8">
          {document.chapters.map((chapter, index) => (
            <section key={index}>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {chapter.number && `第${chapter.number}章　`}
                {chapter.title}
              </h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {chapter.content}
              </div>
              {chapter.annotations && chapter.annotations.length > 0 && (
                <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                  <div className="font-semibold mb-2">注釈</div>
                  {chapter.annotations.map((note, nIndex) => (
                    <div key={nIndex} className="mb-1">• {note}</div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      ) : document.body && (
        <section className="mb-8">
          <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {document.body}
          </div>
        </section>
      )}
      
      {document.epilogue && (
        <section className="mt-8 pt-6 border-t-2 border-gray-300">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">結語</h2>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {document.epilogue}
          </p>
        </section>
      )}
      
      {document.annotations && document.annotations.length > 0 && (
        <section className="mt-8 p-4 bg-gray-50 rounded">
          <h3 className="text-lg font-bold text-gray-800 mb-3">注釈一覧</h3>
          <div className="space-y-2 text-sm text-gray-700">
            {document.annotations.map((annotation, index) => (
              <div key={index}>
                <span className="font-semibold">{annotation.marker}:</span> {annotation.text}
              </div>
            ))}
          </div>
        </section>
      )}
      
      {document.metadata && (
        <footer className="mt-8 pt-4 border-t border-gray-200 text-sm text-gray-600">
          {document.metadata.documentAge && (
            <div>文書年代: {document.metadata.documentAge}</div>
          )}
          {document.metadata.condition && (
            <div>保存状態: {document.metadata.condition}</div>
          )}
          {document.metadata.authenticity && (
            <div>真贋: {document.metadata.authenticity}</div>
          )}
        </footer>
      )}
      
      {document.colophon && (
        <div className="mt-6 text-center text-sm text-gray-500">
          {document.colophon}
        </div>
      )}
    </div>
  )
}